import { useRef, useState } from 'react';

import { useParams } from 'react-router';

import { Grid } from '@mui/material';
import { GridRowId } from '@mui/x-data-grid-pro';
import { useQueryClient } from '@tanstack/react-query';
import { FormikProvider, useFormik } from 'formik';

import { StoreAutoComplete } from 'components/autocompletes';
import { StoreOptionsProps } from 'components/autocompletes/StoreAutoComplete';
import { DownloadModeloArquivoBtn } from 'components/buttons';
import { BbarForm } from 'components/docked/BbarForm';
import { ConfirmModal } from 'components/modal';
import { Block } from 'components/page';
import { SubTitle } from 'components/text';
import { useLoaderContext } from 'context/LoaderContext';
import {
    ConfigPedidoHeaderActions,
    ConfigPedidoImportacaoUploadBtn,
    ConfigPedidoProdutosGrid,
    ConfigPedidoProdutosModal,
} from 'pages/compra/configuracao-pedido/components';
import { ConfigPedidoListProvider } from 'pages/compra/configuracao-pedido/contexts';
import { useConfigPedidoDirty, useMutateProdutos, useQueryProdutos } from 'pages/compra/configuracao-pedido/hooks';
import { ConfigPedidoProduto, ProdutosProps } from 'pages/compra/configuracao-pedido/types';

export function ConfigPedidoProdutosForm() {
    const [loja, setLoja] = useState<StoreOptionsProps | null>(null);
    const [openConfirmModal, setOpenConfirmModal] = useState(false);
    const [openProdutoModal, setOpenProdutoModal] = useState(false);
    const temporaryLoja = useRef<StoreOptionsProps | null>(null);

    const { id } = useParams();
    const queryClient = useQueryClient();
    const { data, isFetching } = useQueryProdutos(loja);
    const { onSubmit, isLoading } = useMutateProdutos(loja);

    useLoaderContext(isFetching || isLoading);

    const formik = useFormik({
        initialValues: data || { configPedidoProdutos: [] },
        onSubmit,
        enableReinitialize: true,
    });

    const { values, setFieldValue, submitForm, dirty } = formik;

    useConfigPedidoDirty(dirty);

    /**
     * Adiciona novos produtos à listagem.
     */
    const addProducts = (products: ProdutosProps[]) => {
        const newProducts = products
            .filter((p) => !values.configPedidoProdutos.some((cp) => cp.produto.idProduto === p.idProduto))
            .map(
                (p): ConfigPedidoProduto => ({
                    produto: p,
                    idProduto: p.idProduto,
                    qtMinimaProduto: p.qtMinimaCompra || 0,
                    qtMaximaProduto: 0,
                    fgShowPeOferta: false,
                    fgExibirSomenteParaClusterLoja: false,
                    configPedidoProdutoClusterLoja: [],
                }),
            );

        setFieldValue('configPedidoProdutos', [...values.configPedidoProdutos, ...newProducts]);
    };

    /**
     * Remove produtos da listagem.
     */
    const removeProducts = (ids: Set<GridRowId>) => {
        setFieldValue(
            'configPedidoProdutos',
            values.configPedidoProdutos.filter((p) => !ids.has(p.produto.idProduto)),
        );
    };

    /**
     * Retorna o texto de confirmação para o modal de confirmação.
     */
    const getConfirmText = () => {
        if (!temporaryLoja.current) return 'limpar a loja selecionada';
        if (!loja && temporaryLoja.current) return 'selecionar uma loja';
        return 'trocar de loja selecionada';
    };

    return (
        <FormikProvider value={formik}>
            <ConfirmModal
                open={openConfirmModal}
                setOpen={setOpenConfirmModal}
                text={`Existem alterações não salvas, gostaria de ${getConfirmText()} assim mesmo?`}
                confirm={() => {
                    setLoja(temporaryLoja.current);

                    setOpenConfirmModal(false);
                }}
            />

            <ConfigPedidoProdutosModal open={openProdutoModal} setOpen={setOpenProdutoModal} handleAdicionarProdutos={addProducts} />

            <Block>
                <SubTitle label="Importar produtos" />

                <Grid container sx={{ justifyContent: 'space-between', gap: 2, width: '100%' }}>
                    <Grid>
                        <ConfigPedidoImportacaoUploadBtn
                            url="/gestao/configpedidoproduto/upload"
                            onSuccessUploadFile={(uploadData) => setFieldValue('configPedidoProdutos', uploadData)}
                        />
                    </Grid>

                    <Grid>
                        <DownloadModeloArquivoBtn dsChave="URL_ARQUIVO_MODELO_PRODUTOS_CONFIG_PEDIDO" />
                    </Grid>
                </Grid>

                <SubTitle sx={{ mt: 2 }} label="Importar descontos" />

                <Grid container sx={{ justifyContent: 'space-between', gap: 2, width: '100%' }}>
                    <Grid>
                        <ConfigPedidoImportacaoUploadBtn
                            url="/gestao/configpedidoprodutoofertaloja/upload"
                            onSuccessUploadFile={() => {
                                setLoja(null);

                                queryClient.invalidateQueries({ queryKey: ['config-pedido-produtos', id, null] });
                            }}
                        />
                    </Grid>

                    <Grid>
                        <DownloadModeloArquivoBtn dsChave="URL_ARQUIVO_MODELO_CONFIG_PEDIDO_PRODUTO_OFERTA_LOJA" />
                    </Grid>
                </Grid>
            </Block>

            <Block>
                <SubTitle label="Aplicar descontos por loja" />

                <StoreAutoComplete
                    size="small"
                    defaultStore={false}
                    value={loja}
                    onChange={(_, value) => {
                        if (dirty) {
                            temporaryLoja.current = value;

                            setOpenConfirmModal(true);
                        } else {
                            setLoja(value);
                        }
                    }}
                    requestOptions={{
                        url: '/gestao/loja/findbyidsconfigpedido',
                        sort: [{ property: 'idLoja', direction: 'ASC' }],
                        filter: [{ property: 'idConfigPedido', operator: 'eq', value: id }],
                    }}
                />
            </Block>

            <ConfigPedidoListProvider
                onAdd={() => setOpenProdutoModal(true)}
                onRemove={removeProducts}
                searchPlaceholder="Código, nome ou categoria do produto..."
                addDisabled={Boolean(loja)}
                removeDisabled={Boolean(loja)}
            >
                <Block>
                    <SubTitle label="Produtos" />

                    <ConfigPedidoHeaderActions />

                    <ConfigPedidoProdutosGrid loja={loja} onRemoveProduto={removeProducts} />
                </Block>
            </ConfigPedidoListProvider>

            <BbarForm submitForm={submitForm} />
        </FormikProvider>
    );
}
